import { useCallback, useMemo, useState } from 'react'
import { flushSync } from 'react-dom'
import { Alert, Button, Card, Form, Input, Select, Table, TableGroup, TableHeader, Typography } from '@musinsa/pbo-core'
import type { PboTableColumn } from '@musinsa/pbo-core'
import PageHeader from '@/components/pbo/PageHeader'
import { useGetWarehouseNameOptions } from '@/services/inventory/inventoryStatus/useInventoryStatusQueries'
import { useGetMasterShipperOptions } from '@/services/master/userManagement/useUserManagementQueries'
import { GetSlsInventoryStatusReq } from '@/services/inventory/slsInventoryStatus/types'
import { useGetSlsInventoryStatus } from '@/services/inventory/slsInventoryStatus/useSlsInventoryStatusQueries'
import { PboScope } from '@/pages/inventory/inventoryStatus/components/PboScope'
import { sumSlsInventoryRows, toSlsInventoryTableRows } from '@/pages/inventory/slsInventoryStatus/rows'

/** 센터명 '전체'. BE 가 공백을 활성 센터 전체로 읽는다(`whNm.isNullOrBlank()`). */
const WAREHOUSE_ALL = ''
const EMPTY_REQ: GetSlsInventoryStatusReq = { barcode: '' }

// 좌측정렬 + 좁은 폭 — 우측정렬 110px 이면 값이 화면 오른쪽 끝에 붙어 좌측 센터명/바코드와 시선이 끊긴다(LOGISPRD-12302).
const qtyColumn = (id: string, title: string): PboTableColumn => ({
  id,
  title,
  dataIndex: id,
  align: 'left',
  width: 80,
  render: (value) => (typeof value === 'number' ? value.toLocaleString() : '-'),
})

const COLUMNS: PboTableColumn[] = [
  { id: 'whNm', title: '센터명', dataIndex: 'whNm', width: 120 },
  { id: 'locationCode', title: '센터코드', dataIndex: 'locationCode', width: 100 },
  { id: 'shipperCode', title: '화주코드', dataIndex: 'shipperCode', width: 120 },
  { id: 'barcode', title: '바코드', dataIndex: 'barcode', width: 160 },
  qtyColumn('totalQty', '전체수량'),
  qtyColumn('unavailableQty', '비가용수량'),
  qtyColumn('allocationQty', '할당수량'),
  qtyColumn('remainingQty', '잔여수량'),
]

// BE meta.message 우선, 빈 body 응답(게이트웨이/인프라 차단 등)은 HTTP 상태를 함께 노출
const apiErrMessage = (e: unknown): string => {
  const resp = (e as { response?: { status?: number; data?: { meta?: { message?: string } } } })?.response
  return resp?.data?.meta?.message ?? (resp?.status ? `조회에 실패했습니다. (HTTP ${resp.status})` : '조회에 실패했습니다.')
}

/**
 * 재고현황(SLS) — 메뉴코드 ST06 (LOGISPRD-12147).
 *
 * ST02 재고현황이 `OPR_INV` 만 읽어 "IDB 엔 있는데 SLS 엔 없다"를 확인할 수단이 없던 것을 메운다.
 * 비교는 ST02 화면과 눈으로 한다 — 이 화면은 SLS 값만 보여준다.
 *
 * **IVL 이관 전까지의 임시 도구다**(BE LLD §1.1). 그래서 엑셀·서버 페이징·조회조건 세션 저장을 넣지 않았다.
 * 결과가 센터 수만큼(수십 행) 이라 한 화면에 다 들어간다.
 */
function SlsInventoryStatus() {
  const [form] = Form.useForm()
  const [queryParams, setQueryParams] = useState<GetSlsInventoryStatusReq>(EMPTY_REQ)

  const { options: warehouseOptions } = useGetWarehouseNameOptions()
  // 화주 드롭다운 — label=화주명 / value=화주코드. 화주명만 내려주는 `useGetShipperNames` 를 쓰면
  // 고른 화주명이 그대로 shipperCode 로 나가 항상 조회 실패한다(LOGISPRD-12297 이 드롭다운을 걷어낸 원인).
  const { data: shipperOptions = [], isLoading: isShipperOptionsLoading } = useGetMasterShipperOptions()

  const warehouseSelectOptions = useMemo(() => [{ value: WAREHOUSE_ALL, label: '전체' }, ...warehouseOptions], [warehouseOptions])

  const { data, isFetching, isError, error, refetch } = useGetSlsInventoryStatus(queryParams)

  const handleSearch = useCallback(
    async (values: { barcode: string; shipperCode?: string; whNm?: string }) => {
      // queryParams 커밋 전에 refetch 하면 이전 조건으로 조회된다 — flushSync 로 먼저 커밋한다.
      flushSync(() =>
        setQueryParams({
          barcode: values.barcode.trim(),
          shipperCode: values.shipperCode || undefined,
          whNm: values.whNm || undefined,
        }),
      )
      await refetch()
    },
    [refetch],
  )

  const rows = useMemo(() => toSlsInventoryTableRows(data?.centers ?? []), [data])
  // 걸러진 행은 네 수량이 전부 0 이라(rows.ts) 표에 남은 행만 더해도 전 센터 합계와 같다.
  const totals = useMemo(() => sumSlsInventoryRows(rows), [rows])

  return (
    <PboScope>
      <PageHeader title="재고현황(SLS)" />

      <Card>
        <Form
          form={form}
          layout="inline"
          initialValues={{ whNm: WAREHOUSE_ALL }}
          onFinish={handleSearch}
        >
          <Form.Item label="화주명" name="shipperCode" rules={[{ required: true, message: '화주는 필수입니다.' }]}>
            <Select allowClear showSearch loading={isShipperOptionsLoading} optionFilterProp="label" options={shipperOptions} placeholder="화주 선택" style={{ width: 200 }} />
          </Form.Item>
          <Form.Item label="바코드" name="barcode" rules={[{ required: true, message: '바코드는 필수입니다.' }]}>
            <Input allowClear placeholder="바코드 입력" style={{ width: 200 }} />
          </Form.Item>
          <Form.Item label="센터명" name="whNm">
            <Select options={warehouseSelectOptions} showSearch optionFilterProp="label" style={{ width: 160 }} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={isFetching}>
              조회
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {/* BE 는 한 센터라도 조회에 실패하면 전체를 실패시킨다 — 반쪽 결과를 재고 0 으로 오독하지 않게 하려는 계약이다.
          그래서 에러를 삼키지 않고 사유를 그대로 띄운다. */}
      {isError && <Alert type="error" showIcon message={apiErrMessage(error)} />}

      {/* 센터가 비는 이유(SKU 미해석 / 센터명 오타 / 활성 센터 없음)는 빈 표로는 구분되지 않는다. */}
      {data?.unresolvedReason && <Alert type="warning" showIcon message={data.unresolvedReason} />}

      {/* 전 센터·전 행 합계. 응답에 전 행이 다 들어있고 페이징도 없어 BE 가 합계를 내려줄 이유가 없다 — FE 에서 더한다. */}
      {rows.length > 0 && (
        <Typography.Text strong>
          {`전체수량 ${totals.totalQty.toLocaleString()} / 비가용수량 ${totals.unavailableQty.toLocaleString()} / 할당수량 ${totals.allocationQty.toLocaleString()} / 잔여수량 ${totals.remainingQty.toLocaleString()}`}
        </Typography.Text>
      )}

      {/* skuId 출처가 없으면 재고 0 을 보고 '진짜 없음'인지 '엉뚱한 SKU 조회'인지 가릴 수 없다(BE LLD §5.2). */}
      {data?.skuId && (
        <Typography.Text type="secondary">{`skuId: ${data.skuId} (출처: ${data.skuIdSource ?? '-'})`}</Typography.Text>
      )}

      {/* 수량이 전부 0 인 행은 표에서 걸러내므로(rows.ts), 조회 자체는 됐는데 표가 통째로 비면
          "재고 없음"과 "조회 전"이 똑같이 빈 화면으로 보인다 — 조회 결과였다는 걸 문장으로 남긴다. */}
      {data && !data.unresolvedReason && data.centers.length > 0 && rows.length === 0 && (
        <Alert type="info" showIcon message="해당 바코드, 화주에 해당하는 재고가 아무 센터에도 없습니다." />
      )}

      <TableGroup>
        <TableHeader total={rows.length} suffix="건" showPagination={false} />
        <Table columns={COLUMNS} dataSource={rows} selectable={false} showIndex={false} />
      </TableGroup>
    </PboScope>
  )
}

export default SlsInventoryStatus
